/**
 * apps/automations-portal/src/features/admin/HealthPage.tsx
 *
 * System health board. The endpoint 501s for now — the board degrades to a
 * clear "coming soon" state instead of a spinner that never resolves.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { adminApi, isNotImplemented } from '@/lib/api';
import type { HealthReport, HealthStatus } from '@/lib/types';

const STATUS_ICON: Record<HealthStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  degraded: AlertTriangle,
  down: XCircle,
};

const STATUS_COLOR: Record<HealthStatus, string> = {
  ok: 'text-success',
  degraded: 'text-warning',
  down: 'text-destructive',
};

export function HealthPage() {
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  const [report, setReport] = useState<HealthReport | null>(null);

  useEffect(() => {
    adminApi
      .systemHealth()
      .then((data) => {
        // Normalise before rendering. A health page that throws on an
        // unexpected response is worse than useless — it white-screens the
        // admin console at exactly the moment someone is trying to find out
        // what is broken.
        setReport({
          status: data?.status ?? 'degraded',
          checks: Array.isArray(data?.checks) ? data.checks : [],
          checkedAt: data?.checkedAt ?? new Date().toISOString(),
        });
        setState('ready');
      })
      .catch((err) => {
        setState(isNotImplemented(err) ? 'unavailable' : 'error');
      });
  }, []);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-2xl font-semibold">System health</h1>
        <p className="text-sm text-muted-foreground">The automation engine and its supporting services.</p>
      </div>

      {state === 'loading' && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {state === 'error' && (
        <p className="text-sm text-destructive">Could not reach the health service. Try refreshing the page.</p>
      )}

      {state === 'unavailable' && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Clock className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Health monitoring is coming soon</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Once the automation engine exposes a status endpoint, each service it depends on will show up here with
              its current state.
            </p>
          </CardContent>
        </Card>
      )}

      {state === 'ready' && report && (
        <div className="flex flex-col gap-3">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                {(() => {
                  const Icon = STATUS_ICON[report.status];
                  return <Icon className={`h-5 w-5 ${STATUS_COLOR[report.status]}`} />;
                })()}
                <CardTitle className="text-base">Overall status: {report.status}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                Checked {new Date(report.checkedAt).toLocaleString('en-GB')}
              </p>
            </CardContent>
          </Card>

          {report.checks.map((check) => {
            const Icon = STATUS_ICON[check.status];
            return (
              <Card key={check.name}>
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${STATUS_COLOR[check.status]}`} />
                    <span className="text-sm font-medium">{check.name}</span>
                  </div>
                  {check.detail && <span className="text-xs text-muted-foreground">{check.detail}</span>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
